/* Tier-0: the SAME engine package the server runs, executing in the
   browser for instant what-ifs (ARCHITECTURE §4). Results are identical
   by construction (deterministic engine, same version) — the server's
   /api/simulate remains the cached, authoritative record. */
import {
  scoreCell, traceCell, overallSuccess, chartSeries, inputsHash,
  evaluatePortfolio, focusedScenarios, interventionStats, pareto,
  type ScenarioId, type ScenarioWeight, type CellResult,
} from '@flourish/engine';
import type { CellTrace, Entity, Strategy } from '@flourish/schema';
import type { ScenarioView } from './api.js';

export function toWeights(scenarios: ScenarioView[]): ScenarioWeight[] {
  return scenarios.map(s => ({ id: s.id as ScenarioId, prob: s.prob, focus: s.focus, hidden: s.hidden }));
}

/* memoized cell scoring — cleared whenever inputs change (cheap enough
   to recompute, but the matrix renders 45+ cells per paint) */
const cellCache = new Map<string, CellResult>();
const traceCache = new Map<string, CellTrace>();

export function clearLocalCache(): void {
  cellCache.clear();
  traceCache.clear();
}

export function cell(entity: Entity, strategy: Strategy, scenarioId: ScenarioId): CellResult {
  const key = `${inputsHash(entity, strategy, scenarioId)}`;
  let hit = cellCache.get(key);
  if (!hit) {
    hit = scoreCell(entity, strategy, scenarioId);
    cellCache.set(key, hit);
  }
  return hit;
}

export function trace(entity: Entity, strategy: Strategy, scenarioId: ScenarioId): CellTrace {
  const key = `${inputsHash(entity, strategy, scenarioId)}`;
  let hit = traceCache.get(key);
  if (!hit) {
    hit = traceCell(entity, strategy, scenarioId);
    traceCache.set(key, hit);
  }
  return hit;
}

/** Single-intervention pseudo-strategy (matrix sub-rows). */
export function ivCell(entity: Entity, strategy: Strategy, iid: string, scenarioId: ScenarioId): CellResult {
  const iv = strategy.interventions.find(x => x.iid === iid);
  const pseudo: Strategy = {
    id: `${strategy.id}~${iid}`, name: 'iv', desc: '', isNoRegret: false, isMostLikely: false,
    interventions: iv ? [iv] : [],
  };
  return cell(entity, pseudo, scenarioId);
}

export { overallSuccess, chartSeries, evaluatePortfolio, focusedScenarios, interventionStats, pareto };
export type { ScenarioId, ScenarioWeight };
